// Client-side router using the History API.
// Pages are lazy-loaded via dynamic import() on each navigation.

import { noteInteractive } from "./cache/bodyPrefetch.js";

const routes = {
  // ── User webmail ──────────────────────────────────────────────────────────
  "/login":         () => import("./pages/login.js"),
  "/":              () => import("./pages/inbox.js"),
  "/compose":       () => import("./pages/compose.js"),
  "/message/:uid":  () => import("./pages/message.js"),
  "/settings":      () => import("./pages/settings.js"),
  "/force-password-change": () => import("./pages/force-password-change.js"),

  // ── Admin + super-admin share one login page ──────────────────────────────
  "/admin/login":              () => import("./pages/admin/login.js"),
  "/admin":                    () => import("./pages/admin/dashboard.js"),
  "/admin/mailboxes":          () => import("./pages/admin/mailboxes.js"),
  "/admin/aliases":            () => import("./pages/admin/aliases.js"),
  "/admin/groups":             () => import("./pages/control-center/groups.js"),
  "/admin/webhooks":           () => import("./pages/admin/webhooks.js"),
  "/admin/domain":             () => import("./pages/admin/domain.js"),
  "/admin/activity":           () => import("./pages/control-center/activity.js"),

  // ── Control center (super admin) ─────────────────────────────────────────
  "/control-center":             () => import("./pages/control-center/dashboard.js"),
  "/control-center/domains":     () => import("./pages/control-center/domains.js"),
  "/control-center/mailboxes":   () => import("./pages/control-center/mailboxes.js"),
  "/control-center/groups":      () => import("./pages/control-center/groups.js"),
  "/control-center/admins":      () => import("./pages/control-center/admins.js"),
  "/control-center/invitations": () => import("./pages/control-center/invitations.js"),
  "/control-center/activity":    () => import("./pages/control-center/activity.js"),
  "/control-center/ai":          () => import("./pages/control-center/ai.js"),
  "/control-center/sso":         () => import("./pages/control-center/sso.js"),

  // ── Shared public ─────────────────────────────────────────────────────────
  "/register": () => import("./pages/register.js"),
  // OAuth consent page for SSO into other business tools.
  "/sso/authorize": () => import("./pages/sso-authorize.js"),
};

export function navigate(path) {
  window.history.pushState({}, "", path);
  render();
}

// Circuit breaker against runaway re-render loops. If render() is asked to
// re-draw the SAME url it just drew under 350ms ago, it's a redundant call
// (a feedback loop), not a navigation - skip it. Real navigation changes the
// path or query string, so it is never throttled.
let _lastRenderUrl = null;
let _lastRenderAt = 0;
// Every render takes a ticket. A render may only write to the DOM while its
// ticket is still the newest, so a slow page can never paint over the page the
// user has since moved to.
let _renderSeq = 0;

export async function render() {
  const app = document.getElementById("app");
  const path = window.location.pathname;
  const url = path + window.location.search;
  const now = Date.now();

  if (url === _lastRenderUrl && now - _lastRenderAt < 350) return; // loop guard
  _lastRenderUrl = url;
  _lastRenderAt = now;
  noteInteractive();   // make the body-prefetcher yield to this navigation

  // Start immediately, even while an earlier render is still awaiting its data.
  // Waiting was the bug: Control Center > Activity blocks ~23s on a cold log
  // scan, and for that whole window a sidebar click moved the URL while the
  // page stayed put, so the only way to get there was to reload the URL by
  // hand. The superseded render is not cancelled, its request is already in
  // flight, but it is disowned: renderInner checks its ticket before it paints.
  const seq = ++_renderSeq;
  await renderInner(app, path, seq);
}

async function renderInner(app, path, seq) {
  // False once a newer navigation has started; this render must then not paint.
  const current = () => seq === undefined || seq === _renderSeq;

  const { loader, params } = match(path);
  let mod;

  if (loader) {
    try {
      mod = await loader();
    } catch (err) {
      // After a deploy, the previous tab still references old chunk hashes
      // that no longer exist. A one-shot reload re-fetches index.html and
      // the new chunk map. Guard with a sessionStorage flag so we don't
      // loop on a genuine missing module.
      const msg = String(err?.message ?? "");
      const isChunkMiss = msg.includes("Failed to fetch dynamically imported module")
                       || msg.includes("Importing a module script failed");
      if (isChunkMiss && !sessionStorage.getItem("mc_reloaded_for_chunks")) {
        sessionStorage.setItem("mc_reloaded_for_chunks", "1");
        window.location.reload();
        return;
      }
      mod = null;
    }
  }
  // Clear the reload guard once a render succeeds.
  if (mod) sessionStorage.removeItem("mc_reloaded_for_chunks");

  if (!mod) {
    try {
      const nf = await import("./pages/not-found.js");
      if (!current()) return;
      app.replaceChildren(nf.default({}));
    } catch {
      // not-found page itself is a stale chunk - last resort: hard reload.
      window.location.reload();
    }
    return;
  }

  if (!current()) return;      // superseded while the page chunk was loading
  const node = await mod.default(params);
  if (!current()) return;      // superseded while the page was fetching its data
  app.replaceChildren(node);
}

function match(path) {
  for (const [pattern, loader] of Object.entries(routes)) {
    const params = matchPath(pattern, path);
    if (params !== null) return { loader, params };
  }
  return { loader: null, params: {} };
}

function matchPath(pattern, path) {
  const pp = pattern.split("/");
  const up = path.split("/");
  if (pp.length !== up.length) return null;
  const params = {};
  for (let i = 0; i < pp.length; i++) {
    if (pp[i].startsWith(":")) {
      params[pp[i].slice(1)] = decodeURIComponent(up[i] ?? "");
    } else if (pp[i] !== up[i]) {
      return null;
    }
  }
  return params;
}

export function getQuery() {
  const out = {};
  new URLSearchParams(window.location.search).forEach((v, k) => { out[k] = v; });
  return out;
}

// Handle browser back/forward
window.addEventListener("popstate", render);
